import React, { createContext, useReducer, useContext, ReactNode, useEffect, ReactElement } from 'react';
import useDictionary from '../Hooks/useDictionary';
import useLogin from '../Hooks/useLogin';
import FilteredZooConstituentDto, { FilteredZooMembershipDto } from '../Models/FilteredZooConstituentDto';
import useMemberships from '../Hooks/useMemberships';
import ConstituentDto from '../Models/ConstituentDto';
import { ButtonCtaDto } from '../Models/ButtonCtaDto';

// Define the account state type
interface AccountState {
  isAuthenticated: boolean | null;
  selectedTab?: string | null;
  dictionary: Map<string, string>;
  buttonCtas?: Map<string, ButtonCtaDto>;
  error?: ReactElement | string;
    memberships?: FilteredZooConstituentDto;
    currentMembership?: FilteredZooMembershipDto; // Assuming currentPass is of the same type as memberships
  household?: ConstituentDto; // Assuming household is of the same type as memberships
}

// Define action types
type AccountAction =
  | { type: 'authenticated', data: boolean }
  | { type: 'selected-tab', data?: string | null }
  | { type: 'error', data: ReactElement | string }
  | { type: 'load', data: { dictionary: Map<string, string>, memberships: FilteredZooConstituentDto, household: ConstituentDto, buttonCtas: Map<string, ButtonCtaDto> } }

const initialState: AccountState = {
  isAuthenticated: null,
  selectedTab: 'overview', // Default tab
  dictionary: new Map<string, string>(),
};

// Create the account reducer
const accountReducer = (state: AccountState, action: AccountAction): AccountState => {
    const newState = { ...state };
    const { type, data } = action
    switch (type) {
        case 'authenticated':
            newState.isAuthenticated = data;
            break;
        case "selected-tab":
            newState.selectedTab = data;
            break;
        case 'error':
            newState.error = data;
            break;
        case 'load':
            newState.dictionary = data.dictionary;
            newState.memberships = data.memberships;
            newState.memberships.actionableMemberships.forEach((membership) => {
                membership.parsedExpirationDate = new Date(membership.expirationDate);
                membership.parsedRenewalDate = new Date(membership.renewalDate);
                membership.forRenewal = true;
            })
            newState.memberships.nonActionableMemberships.forEach((membership) => {
                membership.parsedExpirationDate = new Date(membership.expirationDate);
                membership.parsedRenewalDate = new Date(membership.renewalDate);
            })

            let allMemberships = [...data.memberships.nonActionableMemberships, ...data.memberships.actionableMemberships]
              .filter(x=>x.parsedExpirationDate > new Date())
              .sort((a, b) => {
                return b.parsedExpirationDate.getTime() - a.parsedExpirationDate.getTime();
              });

            newState.currentMembership = allMemberships?.[0] || undefined; // Get the most recent membership

            newState.household = data.household;
            newState.buttonCtas = data.buttonCtas;
            break;
    }

    return newState;
};

// Create the context interfaces
interface AccountContextProps extends AccountState {
  dispatch: React.Dispatch<AccountAction>;
}

// Create the context
const AccountContext = createContext<AccountContextProps | undefined>(undefined);

// Create the provider component
interface AccountProviderProps {
  children: ReactNode;
}

export const AccountProvider: React.FC<AccountProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  const { getDictionary, getButtonCtas } = useDictionary();

  const { getMemberships, getPrimaryHousehold } = useMemberships();

  const { redirectTNewLogin } = useLogin();

  useEffect(() => {
    try {
      // check 
      fetch(`${window.baseApiUrl}/api/tessitura/logged-in`, {
        'credentials': 'include',
      }).then((response) => {
        return response.json();
      }).then((data) => {
        console.log("IsLoggedIn", data);
        if (data.loggedIn) {
          dispatch({
            type: 'authenticated',
            data: true
          });
          return true;
        } else {
          dispatch({
            type: 'authenticated',
            data: false
          });
          setTimeout(() => {redirectTNewLogin(window.baseApiUrl)}, 3000);
          return false;
        }
      }).then((result) => {
        // get the dictionary
        result && Promise.all([
          // @ts-ignore
          getDictionary(window.baseApiUrl, window.apiCulture),
          getMemberships(window.baseApiUrl, true, true),
          getPrimaryHousehold(window.baseApiUrl),
          getButtonCtas(window.baseApiUrl, window.apiCulture)
        ]).then(([dictionary, memberships, household, buttonCtas]) => {
          dispatch({
            type: 'load',
            data: {
              dictionary,
              memberships,
              buttonCtas,
              household
            }
          });
        });
      })
    } catch (e: any) {
      dispatch({
        type: 'error',
        data: e.toString()
      })
    }
  }, []);

  return (
    <AccountContext.Provider value={{ ...state, dispatch }}>
      {children}
    </AccountContext.Provider>
  );
};

// Create a custom hook to use the account context
export const useAccount = (): AccountContextProps => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};

export default AccountContext;